<script setup lang="ts">
  import type { Email, EmailSender } from '@api/models';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, ref } from 'vue';

  import { Button } from '@ui/components/button';
  import { FormControl } from '@ui/components/form';
  import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import LinkEmailDialog from '@/components/email-senders/LinkEmailDialog.vue';
  import { useEmailSenders, useProfile } from '@/store';

  const props = defineProps<{
    email?: Email | null;
    useFormControl?: boolean;
    disabled?: boolean;
    clearable?: boolean;
    sharedOnly?: boolean;
    showConfigureLink?: boolean;
  }>();
  const emailSender = defineModel<EmailSender | null>({ default: null });
  const loading = ref(true);
  const selecting = ref(false);

  const linkEmailDialogRef = ref();

  onBeforeMount(async () => {
    await emailSenderStore.fetchEmailSenders();
    loading.value = false;
  });

  const emailSenderStore = useEmailSenders();
  const { visibleEmailSenders } = storeToRefs(emailSenderStore);

  const profileStore = useProfile();
  const { profile } = storeToRefs(profileStore);

  const allEmailSenders = computed<EmailSender[]>(() => {
    const base = visibleEmailSenders.value.slice();
    if (emailSender.value) {
      const idx = base.findIndex((es) => es.id === emailSender.value?.id);
      if (idx === -1 && emailSender.value) {
        base.push(emailSender.value);
      }
    }

    return base;
  });

  const displayedEmailSenders = computed<EmailSender[]>(() => {
    if (!props.sharedOnly) return allEmailSenders.value;
    return allEmailSenders.value.filter((es) => es.is_shared);
  });

  const accessibleEmailSenders = computed<EmailSender[]>(() =>
    allEmailSenders.value.filter((es) => canAccessEmailSender(es)),
  );

  const selectEmailSender = (senderId?: string | null) => {
    selecting.value = true;
    if (!senderId) {
      if (props.clearable) {
        emailSender.value = null;
      }
    } else {
      const idx = allEmailSenders.value.findIndex((es) => es.id === senderId);
      if (idx !== -1) {
        const sender = allEmailSenders.value[idx];
        if (canUseEmailSender(sender)) {
          emailSender.value = sender;
        }
      }
    }

    selecting.value = false;
  };

  const canAccessEmailSender = (emailSender: EmailSender): boolean => {
    return emailSender.is_shared || emailSender.user_id === profile.value.id;
  };

  const isEmailSenderPartOfThread = (emailSender: EmailSender): boolean => {
    return !props.email || props.email.hasRemoteLink(emailSender);
  };

  const canUseEmailSender = (emailSender: EmailSender): boolean => {
    return (
      !emailSender.is_nylas_grant_expired &&
      canAccessEmailSender(emailSender) &&
      isEmailSenderPartOfThread(emailSender)
    );
  };
</script>

<template>
  <Select
    :model-value="emailSender?.id"
    v-bind="$attrs"
    :disabled="loading || disabled || false"
    @update:model-value="selectEmailSender"
  >
    <FormControl v-if="useFormControl">
      <SelectTrigger class="w-full">
        <SelectValue />
      </SelectTrigger>
    </FormControl>

    <SelectTrigger v-else class="w-full">
      <SelectValue />
    </SelectTrigger>

    <SelectContent class="overflow-hidden">
      <SelectGroup class="max-h-[200px] overflow-y-auto">
        <Tooltip
          v-for="sender in displayedEmailSenders"
          :key="sender.id"
          :disabled="canUseEmailSender(sender)"
        >
          <TooltipTrigger as-child>
            <span :tabindex="0">
              <SelectItem :value="sender.id" :disabled="!canUseEmailSender(sender)">
                <span
                  :class="
                    cn(
                      !canAccessEmailSender(sender)
                        ? 'opacity-50'
                        : sender.is_nylas_grant_expired
                          ? 'text-destructive'
                          : '',
                    )
                  "
                >
                  {{ sender.send_as ?? sender.email }}
                </span>
              </SelectItem>
            </span>
          </TooltipTrigger>

          <TooltipContent>
            <span v-if="!canAccessEmailSender(sender)">
              You are not authorized to use this email address.
            </span>

            <span v-else-if="sender.is_nylas_grant_expired">
              You need to relink this email in
              <RouterLink :to="{ name: 'settings.connectors.connected-email' }" class="text-blue">
                settings.
              </RouterLink>
            </span>

            <span v-else-if="!isEmailSenderPartOfThread(sender)">
              This email address is not part of this thread.
            </span>
          </TooltipContent>
        </Tooltip>
      </SelectGroup>

      <SelectGroup v-if="clearable && emailSender && allEmailSenders.length" class="border-t">
        <SelectItem
          :value="null"
          class="text-center flex justify-center items-center py-1.5 font-medium"
          @click="selecting = true"
        >
          {{ selecting ? '' : 'Clear' }}
        </SelectItem>
      </SelectGroup>

      <SelectGroup v-if="showConfigureLink" class="border-t">
        <RouterLink :to="{ name: 'settings.connectors.connected-email' }">
          <SelectItem
            value="__configure__"
            class="text-center flex justify-center items-center gap-1.5 py-1.5 text-muted-foreground"
          >
            <i class="far fa-gear" />
            Configure
          </SelectItem>
        </RouterLink>
      </SelectGroup>

      <SelectGroup
        v-else-if="!sharedOnly && profile.canLinkEmail && !accessibleEmailSenders.length"
      >
        <Button
          class="text-center flex justify-center items-center py-1.5 font-medium w-full"
          variant="ghost"
          size="sm"
          @click="linkEmailDialogRef?.open()"
        >
          Link An Email Address
        </Button>
      </SelectGroup>
    </SelectContent>
  </Select>

  <LinkEmailDialog
    ref="linkEmailDialogRef"
    @email-link-complete="() => emailSenderStore.fetchEmailSenders(true)"
  />
</template>

<style scoped></style>
